import React from 'react';
import PanelErrorBoundary from '../shared/PanelErrorBoundary';
import { confidenceTreatment } from './lib/confidence';

/**
 * The shell every Search Console panel renders inside, and the single place five of
 * the fixed rules are enforced:
 *
 *   §9  `confidence: "insufficient"` → card + title + sentence, and NO evidence.
 *       Gated here rather than inside each evidence component so a panel added later
 *       cannot forget it and draw an empty axis.
 *   §4  The sentence leads the card and is its largest element — larger than the
 *       title, larger than any number the evidence draws.
 *   §11 `sentence === null` → title alone. No "No summary available" placeholder;
 *       the front end never writes its own sentence.
 *   §5  `salience` and `sentence_source` are on the panel object and are never read.
 *   §3  Collapsed by default, expandable in place. Collapsed is the sentence and at
 *       most one visual; expanded is where the depth lives.
 *
 * ── Card anatomy is the house one (§4) ──────────────────────────────────────────
 * §4: "Match the other dashboard pages… consistency across the dashboard wins over this
 * page having its own look." So the shell, the header strip and the disclosure control
 * are the same ones `AnalysisPanel` gives Competitive Intelligence — a header row with
 * the panel name on the left and a chevron on the right that rotates when open, over a
 * body separated by a hairline rule.
 *
 * The one deliberate difference from `AnalysisPanel`: there, opening the header reveals
 * the entire body. Here the sentence and its one visual stay visible when collapsed,
 * because §3 makes the collapsed card "the entire page for an owner". So the chevron
 * governs the DEEP half only, and the header strip carries the title rather than the
 * sentence — long prose makes a poor click target and a worse accessible name.
 *
 * ── Why the control can be absent ───────────────────────────────────────────────
 * `expandable` is false when opening the card would change nothing (see
 * `evidenceFor.hasDepth`). A disclosure that opens onto an unchanged card is
 * indistinguishable, to the reader, from one that is broken — so those panels render a
 * plain header with no chevron at all, exactly as `AnalysisPanel` does when it is told
 * not to offer a toggle.
 *
 * `emphasis: "notable"` gets a left accent rule and a chip, both borrowed from the
 * house's accented-card treatment. §12 bans alert styling outright, so "notable" has to
 * read as weight rather than as warning — no red, no icon, no banner.
 *
 * The evidence slot is wrapped in `PanelErrorBoundary` so one malformed panel renders
 * a friendly message in its own card instead of blanking the page.
 *
 * The panel sentence doubles as the text alternative for whatever chart the evidence
 * draws (§4): it is already a complete plain-language description of the same data,
 * which is why the backend writes it. Evidence components read it off the `sentence`
 * prop and hang it on their chart container.
 */
/**
 * The disclosure control: a persistent "Expand" / "Collapse" label ahead of the chevron.
 *
 * The word sits BEFORE the icon and is always visible — not a hover-only tooltip — so the
 * affordance reads as an action at rest, the way the rest of the dashboard's expandable
 * panels present it, rather than a bare rotating glyph that only names itself on hover.
 * The rotation still reinforces direction; the label makes it unambiguous. The label is
 * `aria-hidden`: the button's `aria-expanded` already tells assistive tech the state, and
 * the button's accessible name stays the panel title rather than "…Expand".
 */
const Chevron = ({ open }) => (
  <span aria-hidden="true" data-testid="chevron-label" className="flex items-center gap-1.5 flex-shrink-0 text-gray-400 dark:text-gray-500">
    <span className="text-sm">{open ? 'Collapse' : 'Expand'}</span>
    <svg
      className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </span>
);

/**
 * Panels whose body is a grid of already-bordered stat tiles (`StatTile`), so the header
 * rule that divides summary from body on the chart/table panels only doubles up a border
 * here. These skip it.
 */
const TILE_BODY_PANELS = new Set(['indexing_health', 'sitemap_health', 'data_coverage']);

const NotableChip = () => (
  <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-[#32B1FF]/15 dark:bg-[#32B1FF]/20 text-[#32B1FF]">
    Worth knowing
  </span>
);

const PanelCard = ({ panel, expanded = false, onToggle, expandable = true, children }) => {
  const { showEvidence, opacityClass, caption } = confidenceTreatment(
    panel.confidence,
    panel.sample_size,
    panel.sample_size_label,
  );

  const notable = panel.emphasis === 'notable';
  // An insufficient panel has no evidence to deepen, so it gets no control either.
  const canExpand = expandable && showEvidence && Boolean(children) && Boolean(onToggle);

  const title = (
    <div className="flex items-center gap-2 flex-wrap min-w-0">
      <h3
        id={`panel-${panel.panel}-title`}
        className="text-base font-semibold text-gray-900 dark:text-gray-100"
      >
        {panel.title}
      </h3>
      {notable && <NotableChip />}
    </div>
  );

  return (
    <section
      id={`panel-${panel.panel}`}
      data-panel={panel.panel}
      data-emphasis={panel.emphasis}
      data-expanded={expanded ? 'true' : 'false'}
      aria-labelledby={`panel-${panel.panel}-title`}
      // Focusable as a JUMP TARGET only — `-1` keeps it out of the tab order, so nobody
      // tabbing through the page ever lands on a card shell. It exists so the "worth
      // knowing" chips and the `?panel=` deep link can move focus here as well as
      // scrolling: a keyboard reader who follows a jump list and keeps tabbing would
      // otherwise resume from the block they left, several screens above the card they
      // asked for.
      tabIndex={-1}
      // `focus-visible` rather than `focus`: a programmatic `.focus()` after a MOUSE
      // click does not match it, so a chip-clicker sees nothing, while a keyboard reader
      // who followed the same chip gets a ring telling them where they landed.
      className={`scroll-mt-24 bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 ${
        notable ? 'border-l-4 border-l-[#32B1FF]' : ''
      }`}
    >
      {/* Header strip — the house `AnalysisPanel` header, a button when there is
          something to disclose and a plain row when there is not. The button and the
          sentence share a `group`, so an expandable panel's hover highlight covers the
          whole summary (title AND description) rather than stopping at the title row —
          the sentence stays OUTSIDE the button (it is content, not the affordance), but
          reads as part of the same hovered surface. */}
      <div className="group">
        {canExpand ? (
          <button
            type="button"
            onClick={() => onToggle(panel.panel)}
            aria-expanded={expanded}
            data-testid={`toggle-${panel.panel}`}
            className="w-full px-5 py-3 flex items-center justify-between gap-3 text-left group-hover:bg-gray-50 dark:group-hover:bg-gray-800/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset"
          >
            {title}
            <Chevron open={expanded} />
          </button>
        ) : (
          <div className="px-5 py-3 flex items-center gap-3">{title}</div>
        )}

        {/* §11 — no sentence means no line at all. It sits below the header strip and
            outside the control, because it is the card's primary content rather than
            part of the affordance. */}
        {panel.sentence && (
          <p
            data-testid="panel-sentence"
            // House hierarchy (matches Competitive Intelligence's Rate Trends header): the
            // title above is the near-black anchor; the sentence reads as the smaller, muted
            // description line beneath it (12px / text-xs, gray-500). It picks up the header's
            // hover tint only when the panel is expandable, so the highlight is continuous.
            className={`px-5 -mt-2 pb-4 text-xs font-normal leading-normal text-gray-500 dark:text-gray-400 transition-colors ${
              canExpand ? 'group-hover:bg-gray-50 dark:group-hover:bg-gray-800/40' : ''
            }`}
          >
            {panel.sentence}
          </p>
        )}
      </div>

      {/* §9 — insufficient confidence renders the card above and stops.
          A hairline rule always divides the header summary (title + description) from the
          body below it — the house `AnalysisPanel` treatment (see Image #34's "Demand &
          Timing") — in both the collapsed and expanded states. Panels whose body is a grid
          of already-bordered stat tiles skip it, so the two borders don't double up. */}
      {showEvidence && children && (
        <div className={`px-5 pb-5 ${TILE_BODY_PANELS.has(panel.panel) ? '' : 'pt-4 border-t border-gray-100 dark:border-gray-800'} ${opacityClass}`}>
          <PanelErrorBoundary label={panel.panel}>{children}</PanelErrorBoundary>
        </div>
      )}

      {showEvidence && caption && (
        <p className="px-5 pb-4 text-xs text-gray-400 dark:text-gray-500">{caption}</p>
      )}
    </section>
  );
};

export default PanelCard;
